// src/components/Creator/AuthorProfileView.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Globe, Mail, Sparkles, User, Building2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import CreatorNav from '../CreatorNav';
import CreatorTopMenuBar from '../CreatorTopMenuBar';
import CreatorMobileBottomNav from '../CreatorMobileBottomNav';

const AuthorProfileView = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${BACKEND_URL}/api/creator/author-profile`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.exists === false || !response.data.bio_ai_enhanced) {
        toast.error('AI-Enhanced profile not found');
        navigate('/creator');
        return;
      }
      
      setProfile(response.data);
    } catch (error) {
      console.error('Error fetching author profile:', error);
      toast.error('Failed to load author profile');
      navigate('/creator');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-50">
        <CreatorNav />
        <CreatorTopMenuBar />
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="text-slate-400">Loading profile...</div>
        </div>
        <CreatorMobileBottomNav />
      </div>
    );
  }

  if (!profile || !profile.bio_ai_enhanced) {
    return null; // Already redirected
  }

  const displayName = profile.pen_name || `${profile.first_name} ${profile.last_name}`;
  const { short_bio, long_bio, tagline, last_generated_at } = profile.bio_ai_enhanced;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      {/* Navigation */}
      <CreatorNav />
      <CreatorTopMenuBar />
      
      {/* Main Content with mobile bottom padding */}
      <div className="pb-20 md:pb-0">
        <div className="mx-auto max-w-4xl px-4 py-8 sm:py-12">
          {/* Back button */}
          <Button
            onClick={() => navigate('/creator')}
            variant="ghost"
            className="mb-6 text-slate-300 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>

          {/* Header Card */}
          <Card className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 border-slate-700 mb-6">
            <CardContent className="pt-6">
              <div className="flex items-start gap-6">
                {/* Profile Image */}
                {profile.headshot_image_url ? (
                  <img
                    src={`${BACKEND_URL}${profile.headshot_image_url}?t=${Date.now()}`}
                    alt={displayName}
                    className="h-24 w-24 sm:h-32 sm:w-32 rounded-full object-cover border-4 border-purple-500/30 shadow-xl"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="h-24 w-24 sm:h-32 sm:w-32 rounded-full bg-slate-800 border-4 border-purple-500/30 flex items-center justify-center">
                    <User className="h-12 w-12 text-slate-500" />
                  </div>
                )}

                {/* Name & Tagline */}
                <div className="flex-1">
                  <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
                    {displayName}
                  </h1>
                  
                  {tagline && (
                    <p className="text-lg sm:text-xl text-purple-300 italic mb-4">
                      "{tagline}"
                    </p>
                  )}

                  {/* Contact info */}
                  <div className="flex flex-wrap gap-4 text-sm text-slate-300">
                    {profile.website_url && (
                      <a
                        href={profile.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 hover:text-blue-400 transition-colors"
                      >
                        <Globe className="h-4 w-4" />
                        Website
                      </a>
                    )}
                    {profile.contact_email && (
                      <a
                        href={`mailto:${profile.contact_email}`}
                        className="flex items-center gap-1 hover:text-blue-400 transition-colors"
                      >
                        <Mail className="h-4 w-4" />
                        Contact
                      </a>
                    )}
                    {profile.publisher_name && (
                      <div className="flex items-center gap-1">
                        <Building2 className="h-4 w-4" />
                        {profile.publisher_url ? (
                          <a
                            href={profile.publisher_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-blue-400 transition-colors"
                          >
                            {profile.publisher_name}
                          </a>
                        ) : (
                          <span>{profile.publisher_name}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Logo */}
              {profile.logo_image_url && (
                <div className="mt-6 flex justify-center">
                  <img
                    src={`${BACKEND_URL}${profile.logo_image_url}?t=${Date.now()}`}
                    alt="Author logo"
                    className="h-16 object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}

              {/* AI Badge */}
              <div className="mt-6 flex items-center justify-center gap-2 text-xs text-purple-400">
                <Sparkles className="h-3 w-3" />
                <span>AI-Enhanced Profile</span>
                <span className="text-slate-500">•</span>
                <span className="text-slate-400">
                  Generated {new Date(last_generated_at).toLocaleDateString()}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Short Bio */}
          <Card className="bg-slate-900/50 border-slate-700 mb-6">
            <CardContent className="pt-6">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <span className="text-purple-400">•</span>
                About {profile.first_name || displayName}
              </h2>
              <div className="prose prose-invert max-w-none">
                <p className="text-slate-300 leading-relaxed whitespace-pre-wrap">
                  {short_bio}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Long Bio */}
          <Card className="bg-slate-900/50 border-slate-700 mb-6">
            <CardContent className="pt-6">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <span className="text-blue-400">•</span>
                The Full Story
              </h2>
              <div className="prose prose-invert max-w-none">
                <div className="text-slate-300 leading-relaxed space-y-4 whitespace-pre-wrap">
                  {long_bio}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <CreatorMobileBottomNav />
    </div>
  );
};

export default AuthorProfileView;
